import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, Phone, ChevronDown } from 'lucide-react';
import { Button } from './ui/button';
import { Sheet, SheetContent, SheetTrigger, SheetClose, SheetTitle, SheetDescription } from './ui/sheet';
import * as VisuallyHidden from '@radix-ui/react-visually-hidden';

const LOGO_URL = "https://customer-assets.emergentagent.com/job_a2fe651e-5a75-4c03-b574-7770491eb762/artifacts/uqksscxp_7cfcc9_f4e11a8d48f74ecc9017bb515bda0ff5~mv2.png.avif";

const navigation = [
  { name: 'Home', href: '/' },
  { name: 'About', href: '/about' },
  { name: 'Services', href: '/services' },
  { name: 'Team', href: '/team' },
  { name: 'Testimonials', href: '/testimonials' },
  { name: 'Contact', href: '/contact' },
];

export const Navbar = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const isActive = (href) => {
    if (href === '/') return location.pathname === '/';
    return location.pathname.startsWith(href);
  };

  return (
    <header
      className={`sticky top-0 z-50 w-full transition-all duration-300 bg-white ${
        isScrolled 
          ? 'shadow-md' 
          : 'shadow-sm'
      }`}
      data-testid="navbar"
    >
      {/* Top bar with phone */}
      <div className="bg-[#000080] text-white py-2 px-4">
        <div className="container mx-auto flex justify-between items-center text-sm">
          <span className="hidden sm:block">Medicare Certified & CHAP/ACHC Accredited</span>
          <a 
            href="tel:+18773106120" 
            className="flex items-center gap-2 hover:text-[#34D399] transition-colors"
            data-testid="phone-link-top"
          >
            <Phone className="w-4 h-4" />
            <span className="font-semibold">877.310.6120</span>
          </a>
        </div>
      </div>

      {/* Main navigation */}
      <nav className="container mx-auto px-4 md:px-6 lg:px-8 bg-white">
        <div className="flex items-center justify-between h-20 lg:h-24">
          {/* Logo */}
          <Link to="/" className="flex-shrink-0" data-testid="logo-link">
            <img
              src={LOGO_URL}
              alt="Senior Care Solutions"
              className="h-10 lg:h-12 w-auto"
            />
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden lg:flex items-center gap-5">
            {navigation.map((item) => (
              <Link
                key={item.name}
                to={item.href}
                className={`nav-link text-sm font-medium transition-colors whitespace-nowrap ${
                  isActive(item.href)
                    ? 'text-[#00A878]'
                    : 'text-[#334155] hover:text-[#00A878]'
                }`}
                data-testid={`nav-${item.name.toLowerCase()}`}
              >
                {item.name}
              </Link>
            ))}
          </div>

          {/* CTA Buttons */}
          <div className="hidden lg:flex items-center gap-2">
            <a href="tel:+18773106120" data-testid="phone-link-nav">
              <Button 
                variant="outline" 
                className="border-2 border-[#000080] text-[#000080] hover:bg-[#000080] hover:text-white rounded-full px-3 py-1.5 text-xs h-8"
              >
                <Phone className="w-3 h-3 mr-1" />
                Call
              </Button>
            </a>
            <Link to="/contact" data-testid="cta-consultation">
              <Button className="bg-[#00A878] hover:bg-[#065F46] text-white rounded-full px-3 py-1.5 text-xs h-8">
                Consultation
              </Button>
            </Link>
          </div>

          {/* Mobile Menu */}
          <div className="lg:hidden flex items-center gap-3">
            <a href="tel:+15612990100" data-testid="phone-link-mobile">
              <Button 
                size="icon" 
                className="bg-[#00A878] hover:bg-[#065F46] text-white rounded-full"
              >
                <Phone className="w-5 h-5" />
              </Button>
            </a>
            <Sheet open={isOpen} onOpenChange={setIsOpen}>
              <SheetTrigger asChild>
                <Button 
                  variant="outline" 
                  size="icon"
                  className="border-[#000080]"
                  data-testid="mobile-menu-trigger"
                >
                  <Menu className="w-6 h-6 text-[#000080]" />
                </Button>
              </SheetTrigger>
              <SheetContent side="right" className="w-[300px] sm:w-[350px]">
                <SheetTitle className="sr-only">Navigation Menu</SheetTitle>
                <SheetDescription className="sr-only">Mobile navigation menu for Senior Care Solutions</SheetDescription>
                <div className="flex flex-col h-full pt-8">
                  <div className="flex flex-col gap-4">
                    {navigation.map((item) => (
                      <SheetClose asChild key={item.name}>
                        <Link
                          to={item.href}
                          className={`text-lg font-medium py-2 px-4 rounded-lg transition-colors ${
                            isActive(item.href)
                              ? 'bg-[#ECFDF5] text-[#00A878]'
                              : 'text-[#334155] hover:bg-[#F8FAFC]'
                          }`}
                          data-testid={`mobile-nav-${item.name.toLowerCase()}`}
                        >
                          {item.name}
                        </Link>
                      </SheetClose>
                    ))}
                  </div>
                  <div className="mt-auto pb-8 space-y-4">
                    <SheetClose asChild>
                      <Link to="/contact" className="block">
                        <Button className="w-full bg-[#00A878] hover:bg-[#065F46] text-white rounded-full py-6 text-lg">
                          Free Consultation
                        </Button>
                      </Link>
                    </SheetClose>
                    <a href="tel:+15612990100" className="block">
                      <Button 
                        variant="outline" 
                        className="w-full border-2 border-[#000080] text-[#000080] hover:bg-[#000080] hover:text-white rounded-full py-6 text-lg"
                      >
                        <Phone className="w-5 h-5 mr-2" />
                        (561) 299-0100
                      </Button>
                    </a>
                  </div>
                </div>
              </SheetContent>
            </Sheet>
          </div>
        </div>
      </nav>
    </header>
  );
};

export default Navbar;
